// Cloudflare Turnstile — invisible, centralized, BEST-EFFORT CAPTCHA for Supabase auth.
//
// getCaptchaToken() returns a fresh token when the widget solves, or `undefined` if Turnstile
// can't load/solve in time. It NEVER throws and never blocks auth on its own — Supabase decides
// whether a token is actually required (the project-wide CAPTCHA toggle):
//   - CAPTCHA off → a missing token is fine; guests get through even if Turnstile hiccups.
//   - CAPTCHA on  → a valid token passes; a missing token is rejected by Supabase (fail-closed).
// This keeps guest sign-in resilient to a misconfigured/blocked widget while still supporting
// real enforcement when it's switched on. The site key is public; the SECRET lives only in Supabase.

const SITE_KEY: string =
  (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAADegVhJA4phHSAEx';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 8000;

declare global {
  interface Window {
    turnstile?: any;
  }
}

let scriptPromise: Promise<void> | null = null;
let widgetId: string | null = null;
let renderPromise: Promise<void> | null = null;

// Most recently solved, not-yet-consumed token, plus anyone awaiting the next solve.
let currentToken: string | null = null;
let waiters: Array<(token: string | undefined) => void> = [];

function settleWaiters(token: string | undefined) {
  const pending = waiters;
  waiters = [];
  pending.forEach((w) => w(token));
}

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load verification'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function ensureWidget(): Promise<void> {
  if (widgetId !== null) return Promise.resolve();
  if (renderPromise) return renderPromise;
  renderPromise = loadScript().then(() => {
    if (widgetId !== null) return;
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.bottom = '0';
    container.style.right = '0';
    container.style.zIndex = '2147483647';
    document.body.appendChild(container);
    widgetId = window.turnstile!.render(container, {
      sitekey: SITE_KEY,
      appearance: 'interaction-only', // invisible unless a challenge is required
      retry: 'auto',
      callback: (token: string) => {
        currentToken = token;
        settleWaiters(token);
      },
      'expired-callback': () => {
        currentToken = null;
        if (widgetId !== null) window.turnstile!.reset(widgetId);
      },
      'error-callback': () => {
        // Widget failed (e.g. hostname not allowed, network). Release waiters with no token —
        // Supabase will accept that when CAPTCHA is off, or reject it when CAPTCHA is on.
        currentToken = null;
        settleWaiters(undefined);
      },
    });
  });
  return renderPromise;
}

/**
 * Resolve with a fresh single-use Turnstile token, or `undefined` if verification can't be
 * loaded/solved within the timeout. Never throws — Supabase enforces whether a token is required.
 */
export async function getCaptchaToken(): Promise<string | undefined> {
  try {
    await ensureWidget();
  } catch {
    return undefined; // script blocked/offline → let Supabase decide (fine when CAPTCHA is off)
  }

  const consume = (token: string): string => {
    currentToken = null;
    if (widgetId !== null) {
      try {
        window.turnstile!.reset(widgetId); // pre-solve the next token
      } catch {
        /* ignore */
      }
    }
    return token;
  };

  if (currentToken) return consume(currentToken);

  return new Promise<string | undefined>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    const finish = (token: string | undefined) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      waiters = waiters.filter((w) => w !== onSettle);
      resolve(token ? consume(token) : undefined);
    };
    const onSettle = (token: string | undefined) => finish(token);
    timer = setTimeout(() => finish(undefined), TOKEN_TIMEOUT_MS);
    waiters.push(onSettle);
  });
}
